import {
    DeleteTwoTone,
    ReloadOutlined,
    QuestionCircleOutlined,
} from "@ant-design/icons";
import {
    Button,
    Col,
    Form,
    Input,
    Modal,
    notification,
    Popconfirm,
    Row,
    Select,
    Skeleton,
    Tabs,
} from "antd";
import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import HTTP from "../../services/httpService";
import { getUserRole } from "../../services/util";
import CustomTable from "../common/Table";

const { TabPane } = Tabs;
const { Option } = Select;

export default function KasaDevices() {
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const userRole = getUserRole();

    const [is_assigned, setIsAssigned] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    const [tableData, setTableData] = useState([]);
    const [assignDeviceId, setAssignDeviceId] = useState(0);
    const [buttonStatus, setSubmitButton] = useState(false);
    const [formData, setFormData] = useState({
        staff_id,
        is_assigned,
        page: 0,
        limit: 10,
    });
    const [assignDeviceForm] = Form.useForm();

    const {
        status: kasaDeviceStatus,
        data: kasaDeviceRes,
        refetch: getKasaDevices,
    } = useQuery(
        ["kasaDevices", is_assigned],
        async () =>
            await HTTP("/hotels/getKasaDeviceList", {
                ...formData,
                is_assigned,
            }),
    );

    const {
        status: hotelKioskStatus,
        data: hotelKioskRes,
        refetch: getHotelKiosks,
    } = useQuery(
        "hotelKioskData",
        async () => await HTTP("/hotels/getHotelKioskList", { staff_id }),
    );

    useEffect(() => {
        getKasaDevices();
    }, []);

    useEffect(() => {
        setFormData({ ...formData, page: 0, limit: 10, is_assigned });
    }, [is_assigned]);

    useEffect(() => {
        getKasaDevices();
    }, [formData]);

    useEffect(() => {
        if (kasaDeviceStatus === "success") {
            const { data, total } = kasaDeviceRes;
            setTableData(data);
            setTotalRecords(total);
        }
    }, [kasaDeviceRes, kasaDeviceStatus]);

    function changeView() {
        setIsAssigned(!is_assigned);
    }

    async function unAssignDevice(device_id) {
        const { success, message } = await HTTP("/hotels/unassignKasaDevice", {
            staff_id,
            device_id,
        });
        if (success) {
            notification["info"]({ message: message });
            getKasaDevices();
        } else {
            notification["error"]({ message: message });
        }
    }

    function assignDevice(device_id, details = {}) {
        getHotelKiosks();
        setAssignDeviceId(device_id);
        let formValues = { device_id };
        if (Object.keys(details).length > 0) {
            formValues = { ...formValues, ...details, reassign: true };
        }
        assignDeviceForm.setFieldsValue(formValues);
    }

    function handleClose() {
        setAssignDeviceId(0);
        assignDeviceForm.resetFields();
    }

    async function handleAssignSubmit(values) {
        handleClose();
        let apiUrl = "/hotels/assignKasaDevice";
        if (values.reassign) {
            apiUrl = "/hotels/reassignKasaDevice";
        }
        const { success, message } = await HTTP(apiUrl, {
            ...values,
            staff_id,
        });
        if (success) {
            getKasaDevices();
            notification["info"]({ message: message });
        } else {
            notification["error"]({ message: message });
        }
    }

    function handleHotelSelection() {
        assignDeviceForm.setFieldsValue({ kiosk_id: "" });
        setSubmitButton(
            hotelKioskRes?.data.find(
                (h) =>
                    h.hotel_id === assignDeviceForm.getFieldValue("hotel_id"),
            )?.kiosks.length > 0
                ? false
                : true,
        );
    }

    async function deleteDevice(device_id) {
        const { success, message } = await HTTP("/hotels/deleteKasaDevice", {
            staff_id,
            device_id,
        });
        if (success) {
            notification.info({ message });
        } else {
            notification.error({ message });
        }
    }

    function handleTableChange(pagination, _, sorter) {
        const { current, pageSize } = pagination;
        const { order, field } = sorter;
        const others = {
            page: current - 1,
            limit: pageSize,
        };
        if (order !== undefined) {
            others.sorting = order === "ascend" ? 1 : 2;
            others.sort_by = field;
        } else {
            delete formData.sorting;
            delete formData.sort_by;
        }

        const newFormData = {
            ...formData,
            ...others,
        };
        setFormData(newFormData);
    }

    const columns = [
        {
            title: "Device Name",
            key: "device_alias",
            dataIndex: "device_alias",
            render: (item, row) => {
                if (row.has_children) {
                    return (
                        <span>{`${row.child_1_alias}/${row.child_2_alias}`}</span>
                    );
                } else return <span>{item}</span>;
            },
            sorter: () => {},
        },
    ];

    async function refreshKasaDevices() {
        const { success, message } = await HTTP(
            "/hotels/refreshKasaDeviceList",
            { staff_id },
        );
        if (success) {
            notification.info({ message });
            getKasaDevices();
        } else {
            notification.error({ message });
        }
    }

    function getDeviceTypesOptions() {
        if (assignDeviceId) {
            let hasChildren = tableData.filter(
                (item) => item.device_id === assignDeviceId,
            )[0].has_children;
            if (hasChildren) {
                return <Option value={1}>Smart Plug</Option>;
            } else {
                return (
                    <>
                        <Option value={1}>Smart Plug</Option>
                        <Option value={2}>Smart CC</Option>
                    </>
                );
            }
        }
    }

    return (
        <>
            <Row>
                <Col span="23">
                    <Tabs
                        onChange={changeView}
                        destroyInactiveTabPane={true}
                        type="card"
                    >
                        <TabPane key="assigned" tab={"Assignned Devices"}>
                            {kasaDeviceStatus !== "loading" &&
                            kasaDeviceStatus !== "idle" ? (
                                <CustomTable
                                    columns={[
                                        ...columns,
                                        {
                                            title: "Hotel",
                                            key: "hotel_name",
                                            dataIndex: "hotel_name",
                                            sorter: () => {},
                                        },
                                        {
                                            title: "Kiosk",
                                            key: "kiosk_name",
                                            dataIndex: "kiosk_name",
                                            sorter: () => {},
                                        },
                                        {
                                            title: "Actions",
                                            render: (_, record) => (
                                                <>
                                                    <Button
                                                        className="margin-right-10"
                                                        type="primary"
                                                        onClick={() => {
                                                            assignDevice(
                                                                record.device_id,
                                                                record,
                                                            );
                                                        }}
                                                    >
                                                        Reassign
                                                    </Button>
                                                    <Popconfirm
                                                        onConfirm={() =>
                                                            unAssignDevice(
                                                                record.device_id,
                                                            )
                                                        }
                                                        title="Are you sure want to unAssign?"
                                                        icon={
                                                            <QuestionCircleOutlined
                                                                style={{
                                                                    color: "red",
                                                                }}
                                                            />
                                                        }
                                                    >
                                                        <Button>
                                                            Unassign
                                                        </Button>
                                                    </Popconfirm>
                                                </>
                                            ),
                                        },
                                    ]}
                                    tableData={tableData}
                                    noOfrecords={totalRecords}
                                    handleTableChange={handleTableChange}
                                    rowKey="device_id"
                                    className="review-table"
                                />
                            ) : (
                                <Skeleton
                                    active={true}
                                    paragraph={{ rows: 5 }}
                                    title={false}
                                />
                            )}
                        </TabPane>
                        <TabPane key="unassigned" tab={"Unassignned Devices"}>
                            {kasaDeviceStatus !== "idle" &&
                            kasaDeviceStatus !== "loading" ? (
                                <CustomTable
                                    columns={[
                                        ...columns,
                                        {
                                            title: "Actions",
                                            render: (_, record) => (
                                                <>
                                                    <Button
                                                        className="margin-right-10"
                                                        type="primary"
                                                        onClick={() =>
                                                            assignDevice(
                                                                record.device_id,
                                                            )
                                                        }
                                                    >
                                                        Assign
                                                    </Button>
                                                    <Popconfirm
                                                        onConfirm={() =>
                                                            deleteDevice(
                                                                record.device_id,
                                                            )
                                                        }
                                                        title="Are you sure want to delete?"
                                                        icon={
                                                            <QuestionCircleOutlined
                                                                style={{
                                                                    color: "red",
                                                                }}
                                                            />
                                                        }
                                                    >
                                                        <DeleteTwoTone className="actionIcon " />
                                                    </Popconfirm>
                                                </>
                                            ),
                                        },
                                    ]}
                                    tableData={tableData}
                                    noOfrecords={totalRecords}
                                    handleTableChange={handleTableChange}
                                    rowKey="device_id"
                                    className="review-table"
                                />
                            ) : (
                                <Skeleton
                                    title={false}
                                    active={true}
                                    paragraph={{ rows: 5 }}
                                />
                            )}
                        </TabPane>
                    </Tabs>
                </Col>
                <Col span="1">
                    {userRole === "Super Admin" && (
                        <Button
                            style={{ marginLeft: "20px" }}
                            type="primary"
                            icon={<ReloadOutlined />}
                            onClick={() => {
                                refreshKasaDevices();
                            }}
                        />
                    )}
                </Col>
            </Row>

            <Modal
                destroyOnClose
                open={Boolean(assignDeviceId)}
                onCancel={handleClose}
                title={"Assign Device"}
                footer={""}
            >
                <Form onFinish={handleAssignSubmit} form={assignDeviceForm}>
                    <Form.Item
                        name="type"
                        label="Type"
                        rules={[
                            {
                                required: true,
                                message: "Please Select Device Type..!",
                            },
                        ]}
                    >
                        <Select id="type">{getDeviceTypesOptions()}</Select>
                    </Form.Item>
                    <Form.Item
                        name="hotel_id"
                        label="Hotel"
                        rules={[
                            {
                                required: true,
                                message:
                                    "Please Select Hotel To Assign Device..!",
                            },
                        ]}
                    >
                        <Select
                            onChange={handleHotelSelection}
                            filterOption={(input, option) =>
                                option.children
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                            }
                            showSearch
                        >
                            {hotelKioskStatus &&
                                hotelKioskRes?.data.map((h) => (
                                    <Option key={h.hotel_id} value={h.hotel_id}>
                                        {`${h.hotel_id} - ${h.hotel_name}`}
                                    </Option>
                                ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        shouldUpdate={(prevValues, currentValues) =>
                            prevValues.hotel_id !== currentValues.hotel_id
                        }
                    >
                        {({ getFieldValue }) =>
                            hotelKioskRes?.data.find(
                                (h) => h.hotel_id === getFieldValue("hotel_id"),
                            )?.kiosks.length > 0 ? (
                                <Form.Item
                                    name="kiosk_id"
                                    label="Kiosk"
                                    rules={[
                                        {
                                            required: true,
                                            message:
                                                "Please Select Kiosk To Assign Device..!",
                                        },
                                    ]}
                                >
                                    <Select>
                                        {hotelKioskStatus &&
                                            hotelKioskRes?.data
                                                .find(
                                                    (h) =>
                                                        h.hotel_id ===
                                                        getFieldValue(
                                                            "hotel_id",
                                                        ),
                                                )
                                                ?.kiosks?.map((k) => (
                                                    <Option
                                                        key={k.kiosk_id}
                                                        value={k.kiosk_id}
                                                    >
                                                        {k.kiosk_name}
                                                    </Option>
                                                ))}
                                    </Select>
                                </Form.Item>
                            ) : (
                                <>
                                    {assignDeviceForm.getFieldValue(
                                        "hotel_id",
                                    ) && (
                                        <span>
                                            This Hotel does not have any Kiosk
                                            right now.
                                        </span>
                                    )}
                                </>
                            )
                        }
                    </Form.Item>
                    <Form.Item name="device_id" className="hidden">
                        <Input
                            id="device_id"
                            className="upcoming-form-inputs"
                            disabled
                        />
                    </Form.Item>
                    <Form.Item name="reassign" className="hidden">
                        <Input
                            id="reassign"
                            className="upcoming-form-inputs"
                            disabled
                        />
                    </Form.Item>
                    <Form.Item
                        wrapperCol={{
                            offset: 8,
                            span: 16,
                        }}
                    >
                        <Button
                            type="primary"
                            htmlType="submit"
                            disabled={buttonStatus}
                        >
                            Submit
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
}
